import { CardWithBorder } from 'src/components/CardWithBorder';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/Checkbox';
import { PencilSimple, Plus, Trash } from '@phosphor-icons/react';
import { useState } from 'react';
import { OverrideModal } from './OverrideModal';
import { DeleteOverrideModal } from './DeleteOverrideModal';
import { format } from 'date-fns';
import { useContactWindow } from '../../ContactWindowContext';

type DateOverridesProps = {
  formatOverride?: (dateStr: string) => string;
};

export const DateOverrides = ({ formatOverride }: DateOverridesProps) => {
  const { useHolidays, setUseHolidays, blackoutDates, setBlackoutDates } =
    useContactWindow();
  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
  const [newOverrideDate, setNewOverrideDate] = useState<string>('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const formatDate = (dateStr: string) => {
    if (typeof formatOverride === 'function') {
      return formatOverride(dateStr);
    }
    try {
      const date = new Date(`${dateStr}T00:00:00`);
      return format(date, 'MMMM do, yyyy');
    } catch {
      return dateStr;
    }
  };

  const openOverrideModal = (index: number | null = null) => {
    if (index !== null) {
      setEditingIndex(index);
      setNewOverrideDate(blackoutDates[index] || '');
    } else {
      setEditingIndex(null);
      setNewOverrideDate('');
    }
    setIsOverrideModalOpen(true);
  };

  const closeOverrideModal = () => setIsOverrideModalOpen(false);

  const saveOverride = () => {
    if (!newOverrideDate) return;
    setBlackoutDates((prev) => {
      const next = [...prev];
      if (editingIndex !== null && editingIndex >= 0) {
        next[editingIndex] = newOverrideDate;
      } else {
        next.push(newOverrideDate);
      }
      return Array.from(new Set(next));
    });
    setIsOverrideModalOpen(false);
  };

  const openDeleteModal = (index: number) => {
    setDeleteIndex(index);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeleteIndex(null);
  };

  const confirmDeleteOverride = () => {
    if (deleteIndex === null) return;
    setBlackoutDates((prev) => prev.filter((_, i) => i !== deleteIndex));
    setIsDeleteModalOpen(false);
    setDeleteIndex(null);
  };
  return (
    <CardWithBorder>
      <h3 className="mb-2">Date Overrides</h3>
      <p className="text-sm text-neutral-500 mb-4">
        Set blackout dates to pause outreach
      </p>
      <div className="flex items-center gap-3">
        <Checkbox
          checked={useHolidays}
          onChange={(checked) => setUseHolidays(checked)}
          ariaLabel="U.S. Federal Holiday"
        />
        <span>U.S. Federal Holiday</span>
      </div>
      {blackoutDates.length > 0 && (
        <div className="mt-4 space-y-2 max-w-[50%]">
          {blackoutDates.map((date, idx) => (
            <div
              key={`${date}-${idx}`}
              className="relative flex items-center justify-between border border-neutral-200 rounded-md px-3 py-2"
            >
              <span className="text-sm text-neutral-700">
                {formatDate(date)}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="text-error-500"
                  title="Delete"
                  onClick={() => openDeleteModal(idx)}
                >
                  <Trash size={18} />
                </button>
                <button
                  type="button"
                  className="text-neutral-500"
                  title="Edit"
                  onClick={() => openOverrideModal(idx)}
                >
                  <PencilSimple size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4">
        <Button
          variant="outlined"
          size="small"
          onClick={() => openOverrideModal(null)}
        >
          <span className="flex items-center">
            <Plus className="mr-2" size={16} /> Add an override
          </span>
        </Button>
      </div>

      <OverrideModal
        isOpen={isOverrideModalOpen}
        onClose={closeOverrideModal}
        title={
          editingIndex !== null
            ? 'Edit date override'
            : 'Create a date override'
        }
        closeLabel="Cancel"
        confirmLabel={editingIndex !== null ? 'Save' : 'Create Override'}
        dateValue={newOverrideDate}
        onChange={setNewOverrideDate}
        onConfirm={saveOverride}
        confirmDisabled={!newOverrideDate}
      />

      <DeleteOverrideModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteOverride}
        formattedDate={
          deleteIndex !== null ? formatDate(blackoutDates[deleteIndex]) : ''
        }
      />
    </CardWithBorder>
  );
};

export default DateOverrides;
